import React, { useContext, useEffect, useState } from 'react';
import { useApi } from '@shared/services/api';
import { ReviewStep } from '@shared/domain/review';
import {
  LEGAL_CHECK_CATEGORY_LABELS,
  type PersistedLegalCheckFlag,
} from '@shared/domain/legalCheck';
import { UserContext, UserLoadState } from '@/shared/contexts/UserContext';
import { getReviewStepName } from '../../review/helpers/ReviewStepNames';
import './AdminLegalFlagsPanel.scss';

type AdminLegalFlag = PersistedLegalCheckFlag;

interface AdminLegalFlagsPanelProps {
  reviewId: number;
}

// Friendly labels for the field-name column. Mirrors prompts the contributor
// saw at submission time. Kept short so the panel stays scannable.
const FIELD_LABELS: Record<string, string> = {
  commentBuyerWell: 'What the buyer did well',
  commentBuyerBetter: 'What the buyer could have done better',
  commentWinLose: 'Factors that influenced the outcome',
  commentAdvice: 'Advice for the next seller',
  entryAttentionOtherComment: "Entry strategy — 'Other' source description",
  breakthroughChannelOtherComment: "Entry strategy — 'Other' breakthrough channel",
  outreachPainPointsComment: 'Outreach pain points that resonated',
  externalProofLeverageComment: 'External proof leveraged',
  adviceFirstStepComment: 'First step advice for the next AE',
  avoidDoingComment: 'What to avoid next time',
  detractorResistanceComment: 'How detractor resistance showed up',
  detractorStrategiesComment: 'Strategies for handling detractors',
  championComment: 'Notes on working with the champion',
  articulatingCriteriaComment: 'Buyer’s criteria articulation feedback',
  criteriaChangeComment: 'Evaluation criteria changes',
  negotiateTrialComment: 'Negotiating out of a trial',
  securityRequirementsComment: 'Security review requirements',
  roiJustificationKPIsComment: 'KPIs used for ROI justification',
  notableGenComment: 'Legal team’s main points of contention',
  liabilityStrategiesComment: 'Strategies for lower liability/indemnification',
  aiInvolvementComment: 'AI-involvement legal concerns',
  negotiationTipsComment: 'Tips for negotiating with this customer',
};

function fieldLabel(name: string): string {
  return FIELD_LABELS[name] ?? name;
}

function stepLabel(step: ReviewStep | string): string {
  // The API serialises the enum as its numeric value; coerce before lookup.
  const numeric = typeof step === 'number' ? step : Number(step);
  return Number.isNaN(numeric)
    ? String(step)
    : getReviewStepName(numeric as ReviewStep);
}

const AdminLegalFlagsPanel: React.FC<AdminLegalFlagsPanelProps> = ({
  reviewId,
}) => {
  const api = useApi();
  const { user, userLoadState } = useContext(UserContext);
  const [flags, setFlags] = useState<AdminLegalFlag[] | null>(null);
  // Stay in the "loading…" state until the user's idToken is actually
  // available; otherwise we'd fire the request with no Authorization
  // header and the API would reject with 401.
  const [loading, setLoading] = useState(true);
  const [rescanning, setRescanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  const isReady
    = userLoadState === UserLoadState.Authenticated && !!user?.idToken;

  useEffect(() => {
    if (!isReady) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .get(`review/${reviewId}/legal-flags`)
      .then((response: any) => {
        if (cancelled) return;
        if (response?.error || response?.success === false) {
          setError('Could not load AI-scanner flags.');
          return;
        }
        const next = (response?.data?.flags ?? response?.flags ?? []) as AdminLegalFlag[];
        setFlags(next);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load AI-scanner flags.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // `api` from useApi() is a fresh object on every render — including it
    // here would loop the fetch on every state update. We only want to
    // re-fetch when the review changes or the auth state flips to ready.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewId, isReady]);

  // Scroll the matching answer card into view and pulse it briefly so the
  // admin's eye lands on the field the scanner flagged. The id is set on
  // the comment Box in BasicReviewReader as `review-field-<fieldName>`.
  const focusField = (fieldName: string): void => {
    const el = document.getElementById(`review-field-${fieldName}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Restart the animation if the same field is clicked again.
    el.classList.remove('review-field--highlighted');
    // Force a reflow so the animation re-fires.
    void el.offsetWidth;
    el.classList.add('review-field--highlighted');
    window.setTimeout(() => {
      el.classList.remove('review-field--highlighted');
    }, 2500);
  };

  const handleRescan = async (): Promise<void> => {
    setRescanning(true);
    setError(null);
    try {
      const response: any = await api.post(
        `review/${reviewId}/legal-flags/rescan`,
      );
      if (response?.error || response?.success === false) {
        setError('Re-scan failed.');
        return;
      }
      const next = (response?.data?.flags ?? response?.flags ?? []) as AdminLegalFlag[];
      setFlags(next);
    }
    catch {
      setError('Re-scan failed.');
    }
    finally {
      setRescanning(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-flags-panel admin-flags-panel--loading">
        <span className="admin-flags-panel__spinner" aria-hidden="true" />
        Loading AI-scanner observations…
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-flags-panel admin-flags-panel--error">
        <span>{error}</span>
        <button
          type="button"
          className="admin-flags-panel__toggle"
          onClick={handleRescan}
          disabled={rescanning}
        >
          {rescanning ? 'Re-scanning…' : 'Re-scan now'}
        </button>
      </div>
    );
  }

  if (!flags || flags.length === 0) {
    return (
      <div className="admin-flags-panel admin-flags-panel--clean">
        <span>
          <strong>AI scanner:</strong>
          {' '}
          no legal-risk observations on this review.
        </span>
        <button
          type="button"
          className="admin-flags-panel__toggle"
          onClick={handleRescan}
          disabled={rescanning}
        >
          {rescanning ? 'Re-scanning…' : 'Re-scan now'}
        </button>
      </div>
    );
  }

  return (
    <section
      className="admin-flags-panel"
      data-testid="admin-legal-flags-panel"
      aria-label="AI scanner legal-risk observations"
    >
      <header className="admin-flags-panel__header">
        <div>
          <p className="admin-flags-panel__eyebrow">Admin only · AI scanner</p>
          <h2 className="admin-flags-panel__title">
            {flags.length}
            {' '}
            potential legal-risk
            {flags.length === 1 ? ' observation' : ' observations'}
          </h2>
          <p className="admin-flags-panel__subtitle">
            The contributor was shown the same observations at submission
            time. Each maps to a rule covered in their contributor training.
          </p>
        </div>
        <div className="admin-flags-panel__header-actions">
          <button
            type="button"
            className="admin-flags-panel__toggle"
            onClick={handleRescan}
            disabled={rescanning}
          >
            {rescanning ? 'Re-scanning…' : 'Re-scan now'}
          </button>
          <button
            type="button"
            className="admin-flags-panel__toggle"
            onClick={() => setCollapsed(c => !c)}
          >
            {collapsed ? 'Show details' : 'Collapse'}
          </button>
        </div>
      </header>

      {!collapsed && (
        <ul className="admin-flags-panel__list">
          {flags.map(flag => (
            <li
              key={flag.id}
              className="admin-flags-panel__list-item"
            >
              <button
                type="button"
                className={`admin-flags-panel__flag admin-flags-panel__flag--${flag.severity}`}
                data-category={flag.category}
                data-testid="admin-legal-flag"
                onClick={() => focusField(flag.fieldName)}
                aria-label={`Jump to ${fieldLabel(flag.fieldName)} and highlight it`}
              >
                <div className="admin-flags-panel__flag-meta">
                  <span className="admin-flags-panel__chip">
                    {LEGAL_CHECK_CATEGORY_LABELS[flag.category] ?? flag.category}
                  </span>
                  <span className="admin-flags-panel__severity">
                    {flag.severity === 'caution' ? 'Caution' : 'Warn'}
                  </span>
                  <span className="admin-flags-panel__location">
                    {stepLabel(flag.reviewStep)}
                    {' '}
                    ›
                    {' '}
                    {fieldLabel(flag.fieldName)}
                  </span>
                  <span className="admin-flags-panel__jump" aria-hidden="true">
                    Jump to field ↓
                  </span>
                </div>

                <blockquote
                  className="admin-flags-panel__excerpt"
                  data-testid="admin-legal-flag-excerpt"
                >
                  “
                  {flag.excerpt}
                  ”
                </blockquote>

                <p className="admin-flags-panel__explanation">
                  {flag.explanation}
                </p>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default AdminLegalFlagsPanel;
